import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
  InputAdornment,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  ViewList as ViewListIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import weddingEventService from '../../services/weddingEventService';
import DialogHeader from '../Common/DialogHeader';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { hu },
});

const calendarMessages = {
  today: 'Ma',
  previous: 'Előző',
  next: 'Következő',
  month: 'Hónap',
  noEventsInRange: 'Nincs feladat ebben az időszakban',
};

function TasksTab({ event, onRefresh }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const cardSx = {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.light, 0.4)} 100%)`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.primary.dark, 0.06)}, 0 4px 16px ${alpha(theme.palette.primary.dark, 0.04)}`,
    height: '100%',
  };
  const cardContentSx = { p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } }, textAlign: 'center' };
  const statsRowSx = {
    display: { xs: 'grid', sm: 'flex' },
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: { xs: 1.5, sm: 2 },
    mb: { xs: 2, sm: 3 },
    overflowX: { sm: 'auto' },
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    pb: 0.5,
  };
  const statItemSx = { flex: '1 1 0', minWidth: { xs: 0, sm: 130 } };

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: null,
    priority: 'MEDIUM',
    assignedTo: '',
  });

  useEffect(() => {
    fetchTasks();
  }, [event.id]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await weddingEventService.getTasks(event.id);
      if (response.success) {
        setTasks(response.data);
      }
    } catch (err) {
      console.error('Fetch tasks error:', err);
      setError('Hiba történt a feladatok betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    try {
      await weddingEventService.addTask(event.id, {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
        priority: formData.priority,
        assignedTo: formData.assignedTo.trim() || null,
      });

      setAddDialogOpen(false);
      resetForm();
      fetchTasks();
      onRefresh();
    } catch (err) {
      console.error('Add task error:', err);
      alert('Hiba történt a feladat hozzáadása során.');
    }
  };

  const handleEditTask = async () => {
    try {
      await weddingEventService.updateTask(event.id, selectedTask.id, {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
        priority: formData.priority,
        assignedTo: formData.assignedTo.trim() || null,
      });

      setEditDialogOpen(false);
      resetForm();
      setSelectedTask(null);
      fetchTasks();
      onRefresh();
    } catch (err) {
      console.error('Edit task error:', err);
      alert('Hiba történt a feladat frissítése során.');
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await weddingEventService.toggleTask(event.id, task.id);
      fetchTasks();
      onRefresh();
    } catch (err) {
      console.error('Toggle task error:', err);
      alert('Hiba történt a feladat státuszának módosítása során.');
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Biztosan törölni szeretnéd: ${task.title}?`)) {
      return;
    }

    try {
      await weddingEventService.deleteTask(event.id, task.id);
      fetchTasks();
      onRefresh();
    } catch (err) {
      console.error('Delete task error:', err);
      alert('Hiba történt a feladat törlése során.');
    }
  };

  const openEditDialog = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate ? new Date(task.dueDate) : null,
      priority: task.priority,
      assignedTo: task.assignedTo || '',
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      dueDate: null,
      priority: 'MEDIUM',
      assignedTo: '',
    });
  };

  const getPriorityColor = (priority) => {
    const colors = { LOW: 'info', MEDIUM: 'warning', HIGH: 'error', URGENT: 'error' };
    return colors[priority] || 'default';
  };

  const getPriorityLabel = (priority) => {
    const labels = { LOW: 'Alacsony', MEDIUM: 'Közepes', HIGH: 'Magas', URGENT: 'Sürgős' };
    return labels[priority] || priority;
  };

  const getPriorityCalendarColor = (priority) => {
    const colors = { LOW: '#4caf50', MEDIUM: '#1976d2', HIGH: '#ff9800', URGENT: '#f44336' };
    return colors[priority] || '#9e9e9e';
  };

  const calendarEvents = tasks
    .filter(t => t.dueDate)
    .map(t => ({
      id: t.id,
      title: t.title,
      start: new Date(t.dueDate),
      end: new Date(t.dueDate),
      allDay: true,
      resource: t,
    }));

  const tasksWithoutDueDate = tasks.filter(t => !t.dueDate).length;

  const columns = [
    {
      field: 'isCompleted',
      headerName: '',
      width: 60,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleToggleTask(params.row)}
          color={params.value ? 'success' : 'default'}
        >
          {params.value ? <CheckIcon /> : <UncheckedIcon />}
        </IconButton>
      ),
    },
    {
      field: 'title',
      headerName: 'Feladat',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            textDecoration: params.row.isCompleted ? 'line-through' : 'none',
            color: params.row.isCompleted ? 'text.secondary' : 'text.primary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'priority',
      headerName: 'Prioritás',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getPriorityLabel(params.value)}
          color={getPriorityColor(params.value)}
          size="small"
          variant={params.value === 'URGENT' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'dueDate',
      headerName: 'Határidő',
      width: 130,
      valueFormatter: (params) =>
        params.value ? format(new Date(params.value), 'yyyy. MM. dd.') : '-',
      renderCell: (params) => {
        if (!params.value) return '-';
        const dueDate = new Date(params.value);
        const isOverdue = !params.row.isCompleted && dueDate < new Date();
        return (
          <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'}>
            {format(dueDate, 'yyyy. MM. dd.')}
          </Typography>
        );
      },
    },
    {
      field: 'assignedTo',
      headerName: 'Felelős',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'actions',
      headerName: 'Műveletek',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openEditDialog(params.row)} title="Szerkesztés">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteTask(params.row)} color="error" title="Törlés">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const completedTasks = tasks.filter(t => t.isCompleted).length;
  const totalTasks = tasks.length;
  const overdueTasks = tasks.filter(t => !t.isCompleted && t.dueDate && new Date(t.dueDate) < new Date()).length;

  const dialogForm = (
    <Stack spacing={2.5} sx={{ mt: 1 }}>

      {/* ── Feladat ── */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
          Feladat
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Feladat neve *"
            size="small"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            label="Leírás"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </Stack>
      </Box>

      <Divider />

      {/* ── Részletek ── */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
          Részletek
        </Typography>
        <Stack spacing={1.5}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
                <DatePicker
                  label="Határidő"
                  value={formData.dueDate}
                  onChange={(newValue) => setFormData({ ...formData, dueDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioritás</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Prioritás"
                >
                  <MenuItem value="LOW">Alacsony</MenuItem>
                  <MenuItem value="MEDIUM">Közepes</MenuItem>
                  <MenuItem value="HIGH">Magas</MenuItem>
                  <MenuItem value="URGENT">Sürgős</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            label="Felelős személy"
            size="small"
            fullWidth
            value={formData.assignedTo}
            onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
            placeholder="pl. Péter, Anna, Mindketten"
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment> }}
          />
        </Stack>
      </Box>

    </Stack>
  );

  return (
    <Box>
      {/* Stats */}
      <Box sx={statsRowSx}>
        {[
          { value: totalTasks, label: 'Összes feladat' },
          { value: completedTasks, label: 'Teljesített' },
          { value: overdueTasks, label: 'Lejárt', gridSpan: true },
        ].map((stat) => (
          <Box key={stat.label} sx={{ ...statItemSx, gridColumn: stat.gridSpan ? { xs: '1 / -1', sm: 'auto' } : undefined }}>
            <Card sx={cardSx}>
              <CardContent sx={cardContentSx}>
                <Typography variant="h4" fontWeight={700} color="primary.dark" sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Header Actions */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, val) => val && setViewMode(val)}
          size="small"
        >
          <ToggleButton value="list" title="Lista nézet">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="calendar" title="Naptár nézet">
            <CalendarMonthIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Feladat hozzáadása
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Naptár vagy lista nézet */}
      {viewMode === 'calendar' ? (
        <Box>
          <Box
            sx={{
              height: isMobile ? 480 : 650,
              '& .rbc-toolbar': { flexWrap: 'wrap', gap: 0.5, mb: 1 },
              '& .rbc-toolbar button': {
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                padding: isMobile ? '4px 8px' : '6px 12px',
              },
              '& .rbc-event': { borderRadius: '4px', border: 'none' },
              '& .rbc-header': { fontSize: isMobile ? '0.7rem' : '0.875rem' },
            }}
          >
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              culture="hu"
              views={['month']}
              view="month"
              date={calendarDate}
              onNavigate={setCalendarDate}
              onView={() => {}}
              messages={calendarMessages}
              style={{ height: '100%' }}
              eventPropGetter={(calEvent) => {
                const task = calEvent.resource;
                return {
                  style: {
                    backgroundColor: getPriorityCalendarColor(task.priority),
                    opacity: task.isCompleted ? 0.5 : 1,
                    textDecoration: task.isCompleted ? 'line-through' : 'none',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                  },
                };
              }}
              onSelectEvent={(calEvent) => openEditDialog(calEvent.resource)}
            />
          </Box>
          {tasksWithoutDueDate > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {tasksWithoutDueDate} feladatnak nincs határideje, ezért a naptáron nem látható.
            </Alert>
          )}
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {tasks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Nincs még feladat
            </Typography>
          )}
          {tasks.map(task => {
            const isOverdue = !task.isCompleted && task.dueDate && new Date(task.dueDate) < new Date();
            return (
              <Card key={task.id} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Sor 1: Prioritás + Lejárt badge */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                    <Chip
                      label={getPriorityLabel(task.priority)}
                      color={getPriorityColor(task.priority)}
                      size="small"
                      variant={task.priority === 'URGENT' ? 'filled' : 'outlined'}
                    />
                    {isOverdue && (
                      <Chip label="Lejárt!" color="error" size="small" />
                    )}
                  </Box>

                  {/* Sor 2: Toggle + cím */}
                  {/* Kipipálás: legalább 40 px-es érintési felület telefonon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, ml: -1 }}>
                    <IconButton
                      onClick={() => handleToggleTask(task)}
                      color={task.isCompleted ? 'success' : 'default'}
                      aria-label={task.isCompleted ? 'Visszaállítás folyamatban lévőre' : 'Késznek jelölés'}
                      sx={{ width: 40, height: 40, flexShrink: 0 }}
                    >
                      {task.isCompleted ? <CheckIcon /> : <UncheckedIcon />}
                    </IconButton>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        textDecoration: task.isCompleted ? 'line-through' : 'none',
                        color: task.isCompleted ? 'text.secondary' : 'text.primary',
                        flex: 1,
                      }}
                    >
                      {task.title}
                    </Typography>
                  </Box>

                  {/* Sor 3: Határidő + felelős */}
                  {(task.dueDate || task.assignedTo) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75, flexWrap: 'wrap' }}>
                      {task.dueDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 14, color: isOverdue ? 'error.main' : 'text.secondary' }} />
                          <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'}>
                            {format(new Date(task.dueDate), 'yyyy. MM. dd.')}
                          </Typography>
                        </Box>
                      )}
                      {task.assignedTo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{task.assignedTo}</Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Sor 4: Műveletek */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(task)} sx={{ flex: 1, fontSize: '0.75rem' }}>
                      Szerkesztés
                    </Button>
                    {/* Törlés: visszafogott ikon-gomb, hogy ne versenyezzen a Szerkesztéssel */}
                <Tooltip title="Törlés">
                  <IconButton
                    onClick={() => handleDeleteTask(task)}
                    aria-label="Törlés"
                    sx={{ width: 40, height: 40, flexShrink: 0, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '8px', '&:hover': { color: 'error.main', borderColor: 'error.light' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={tasks}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nincs még feladat',
              MuiTablePagination: { labelRowsPerPage: 'Sorok száma:' },
            }}
            sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
          />
        </Box>
      )}

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); resetForm(); }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<AddIcon />}
            title="Feladat Hozzáadása"
            onClose={() => { setAddDialogOpen(false); resetForm(); }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>{dialogForm}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setAddDialogOpen(false); resetForm(); }}>Mégse</Button>
          <Button onClick={handleAddTask} variant="contained" disabled={!formData.title.trim()}>Hozzáadás</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedTask(null); resetForm(); }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<EditIcon />}
            title="Feladat Szerkesztése"
            onClose={() => { setEditDialogOpen(false); setSelectedTask(null); resetForm(); }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>{dialogForm}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedTask(null); resetForm(); }}>Mégse</Button>
          <Button onClick={handleEditTask} variant="contained" disabled={!formData.title.trim()}>Mentés</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TasksTab;
